/**
 * Performance budget and quality tiering.
 *
 * Targets (docs/PERFORMANCE.md): >=30 fps on a mid-range phone browser, <10 s
 * first load, <25 MB assets, <150 draw calls, <500k triangles, <200 MB GPU
 * textures. The last one is not a nicety - iOS Safari kills the WebGL context
 * somewhere near 1 GB and the tab goes white with no error.
 */
import type { WebGLRenderer } from 'three';

export type Quality = 'low' | 'medium' | 'high';

export interface QualityProfile {
  dprCap: number;
  anisotropy: number;
  asphaltTextureSize: number;
  shadows: boolean;
  shadowMapSize: 1024 | 2048;
  postFx: 'grade' | 'full';
  roadRadiusM: number;
  propRadiusM: number;
}

/**
 * One source of truth for every setting that scales with the device. Keeping
 * the profile here prevents a "medium" UI label from quietly meaning different
 * things to materials, shadows and streaming.
 */
export const QUALITY_PROFILES: Readonly<Record<Quality, Readonly<QualityProfile>>> = {
  low: {
    dprCap: 1.25,
    anisotropy: 4,
    asphaltTextureSize: 512,
    shadows: false,
    shadowMapSize: 1024,
    postFx: 'grade',
    roadRadiusM: 300,
    propRadiusM: 350,
  },
  medium: {
    dprCap: 1.5,
    anisotropy: 6,
    asphaltTextureSize: 768,
    shadows: true,
    shadowMapSize: 1024,
    postFx: 'grade',
    roadRadiusM: 350,
    propRadiusM: 425,
  },
  high: {
    dprCap: 2,
    anisotropy: 8,
    asphaltTextureSize: 1024,
    shadows: true,
    shadowMapSize: 2048,
    postFx: 'full',
    roadRadiusM: 450,
    propRadiusM: 500,
  },
};

export interface Budget {
  drawCalls: number;
  triangles: number;
  textureMB: number;
}

export const BUDGET: Budget = { drawCalls: 150, triangles: 500_000, textureMB: 200 };

/**
 * Pick a quality tier. Errs toward `low`: a phone that runs smoothly and
 * ships shadows later beats one that stutters from the first frame.
 */
export function detectQuality(): Quality {
  const coarse = matchMedia('(pointer: coarse)').matches;
  const cores = navigator.hardwareConcurrency ?? 4;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  const saveData = Boolean(
    (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData,
  );
  if (saveData || cores <= 2 || mem <= 2) return 'low';
  if (coarse) return cores >= 8 && mem >= 6 ? 'high' : 'medium';
  return cores >= 6 && mem >= 4 ? 'high' : 'medium';
}

/**
 * Device pixel ratio, capped. Phones report 3-4; rendering a road scene at 3x
 * costs ~9x the fill of 1x for a difference nobody sees at arm's length, and it
 * is the most common single cause of a phone-only frame-rate cliff.
 */
export function pixelRatioCap(quality: Quality): number {
  const dpr = window.devicePixelRatio || 1;
  return Math.min(dpr, QUALITY_PROFILES[quality].dprCap);
}

/** Returns the next smaller visual step; low never oscillates upward. */
export function lowerQuality(quality: Quality): Quality {
  if (quality === 'high') return 'medium';
  return 'low';
}

export interface FrameStats {
  fps: number;
  cpuMs: number;
  gpuMs: number | null;
  drawCalls: number;
  triangles: number;
}

export interface GpuTimer {
  begin(): void;
  end(): void;
  poll(): number | null;
  dispose(): void;
}

/**
 * GPU frame timer with a silent fallback for Safari/older devices. Results are
 * asynchronous: `poll()` returns the newest completed, non-disjoint sample.
 */
export function createGpuTimer(renderer: WebGLRenderer): GpuTimer {
  const gl = renderer.getContext();
  if (typeof WebGL2RenderingContext === 'undefined' || !(gl instanceof WebGL2RenderingContext)) {
    return NO_GPU_TIMER;
  }
  const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as {
    TIME_ELAPSED_EXT: number;
    GPU_DISJOINT_EXT: number;
  } | null;
  if (!ext) return NO_GPU_TIMER;

  const pending: WebGLQuery[] = [];
  let active: WebGLQuery | null = null;
  let latest: number | null = null;
  const settle = () => {
    while (pending.length > 0) {
      const query = pending[0]!;
      if (!gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE)) break;
      pending.shift();
      const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean;
      if (!disjoint) latest = (gl.getQueryParameter(query, gl.QUERY_RESULT) as number) / 1e6;
      gl.deleteQuery(query);
    }
  };
  return {
    begin() {
      // Keep profiling lower-overhead than the work it measures: resolve the
      // previous sample and never queue more than one outstanding query.
      settle();
      if (active || pending.length > 0) return;
      active = gl.createQuery();
      if (active) gl.beginQuery(ext.TIME_ELAPSED_EXT, active);
    },
    end() {
      if (!active) return;
      gl.endQuery(ext.TIME_ELAPSED_EXT);
      pending.push(active);
      active = null;
    },
    poll() {
      settle();
      return latest;
    },
    dispose() {
      if (active) gl.deleteQuery(active);
      for (const query of pending) gl.deleteQuery(query);
      pending.length = 0;
      active = null;
    },
  };
}

const NO_GPU_TIMER: GpuTimer = {
  begin() {},
  end() {},
  poll: () => null,
  dispose() {},
};

export class PerfMonitor {
  private frames = 0;
  private last = performance.now();
  private cpuTotal = 0;
  private readonly gpuTimer: GpuTimer;
  private current: FrameStats = { fps: 0, cpuMs: 0, gpuMs: null, drawCalls: 0, triangles: 0 };
  /** Consecutive seconds spent under target, used to trigger a downgrade. */
  private slowSeconds = 0;

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly targetFps = 30,
  ) {
    this.gpuTimer = createGpuTimer(renderer);
    // Post-processing renders the world plus one or more fullscreen passes.
    // Three resets these counters after each render by default, which made the
    // HUD report only the final triangle. We own the once-per-frame reset.
    this.renderer.info.autoReset = false;
  }

  beginFrame(): void {
    this.renderer.info.reset();
  }

  beginGpu(): void {
    this.gpuTimer.begin();
  }

  endGpu(): void {
    this.gpuTimer.end();
  }

  /** Call once per rendered frame. Returns fresh stats about once a second. */
  tick(cpuMs = 0): FrameStats {
    this.frames++;
    this.cpuTotal += cpuMs;
    const now = performance.now();
    const elapsed = now - this.last;
    if (elapsed >= 1000) {
      const info = this.renderer.info;
      this.current = {
        fps: Math.round((this.frames * 1000) / elapsed),
        cpuMs: this.frames > 0 ? this.cpuTotal / this.frames : 0,
        gpuMs: this.gpuTimer.poll(),
        drawCalls: info.render.calls,
        triangles: info.render.triangles,
      };
      this.frames = 0;
      this.cpuTotal = 0;
      this.last = now;
      this.slowSeconds = this.current.fps < this.targetFps ? this.slowSeconds + 1 : 0;
    }
    return this.current;
  }

  /** True once the frame rate has been under target for several seconds running. */
  get shouldDowngrade(): boolean {
    return this.slowSeconds >= 4;
  }

  acknowledgeDowngrade(): void {
    this.slowSeconds = 0;
  }

  dispose(): void {
    this.gpuTimer.dispose();
    this.renderer.info.autoReset = true;
  }

  /** Budget check for the acceptance criteria. */
  overBudget(): string[] {
    const out: string[] = [];
    if (this.current.drawCalls > BUDGET.drawCalls) {
      out.push(`draw calls ${this.current.drawCalls} > ${BUDGET.drawCalls}`);
    }
    if (this.current.triangles > BUDGET.triangles) {
      out.push(`triangles ${this.current.triangles} > ${BUDGET.triangles}`);
    }
    return out;
  }
}
